import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Search,
  Filter,
  RotateCcw,
  Eye,
  X,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  ChevronUp,
  User,
  Smartphone,
  Mail,
  Calendar,
  CreditCard,
  Clock,
  CheckCircle2,
  AlertCircle,
  XCircle,
  Loader2,
  FileText,
  Users,
  History,
  WalletCards,
  Copy,
  Check,
  ExternalLink,
  Hash,
  CircleDollarSign,
  Activity,
  KanbanSquareDashed,
} from "lucide-react";

import { getDetailedMandateReport } from "../../services/apiService";

/* =========================================================
   Helpers
========================================================= */

const STATUS_CONFIG = {
  ACTIVATED: {
    label: "Activated",
    className: "text-emerald-700 bg-emerald-50 border-emerald-200",
    icon: CheckCircle2,
  },
  CREATED: {
    label: "Created",
    className: "text-blue-700 bg-blue-50 border-blue-200",
    icon: Clock,
  },
  CANCELLED: {
    label: "Cancelled",
    className: "text-red-700 bg-red-50 border-red-200",
    icon: XCircle,
  },
  FAILED: {
    label: "Failed",
    className: "text-red-700 bg-red-50 border-red-200",
    icon: AlertCircle,
  },
  IN_PROGRESS: {
    label: "In Progress",
    className: "text-amber-700 bg-amber-50 border-amber-200",
    icon: Loader2,
  },
  SUCCESS: {
    label: "Success",
    className: "text-emerald-700 bg-emerald-50 border-emerald-200",
    icon: CheckCircle2,
  },
};

const INSTALLMENT_STATUS_CONFIG = {
  CREATED: "text-blue-700 bg-blue-50",
  SUCCESS: "text-emerald-700 bg-emerald-50",
  COLLECTION_INITIATED: "text-emerald-700 bg-emerald-50",
  SETTLEMENT_SUCCESS: "text-emerald-700 bg-emerald-50",
  FAILED: "text-red-700 bg-red-50",
  COLLECTION_FAILED: "text-red-700 bg-red-50",
  PROCESSING: "text-amber-700 bg-amber-50",
  PENDING: "text-orange-700 bg-orange-50",
};

const formatCurrency = (value) => {
  if (value === null || value === undefined || value === "") return "—";

  const number = Number(value);

  if (Number.isNaN(number)) return value;

  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 2,
  }).format(number);
};

const formatDate = (date) => {
  if (!date) return "—";

  const parsed = new Date(date);

  if (Number.isNaN(parsed.getTime())) return date;

  return parsed.toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const formatDateTime = (date) => {
  if (!date) return "—";

  const parsed = new Date(date);

  if (Number.isNaN(parsed.getTime())) return date;

  return parsed.toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const formatTimestamp = (timestamp) => {
  if (!timestamp) return "—";

  const parsed = new Date(Number(timestamp));

  if (Number.isNaN(parsed.getTime())) return "—";

  return formatDateTime(parsed);
};

const maskMobile = (mobile) => {
  if (!mobile) return "—";

  const value = String(mobile);

  if (value.length < 6) return value;

  return `${value.slice(0, 3)}••••${value.slice(-3)}`;
};

const truncate = (value, length = 22) => {
  if (!value) return "—";

  const stringValue = String(value);

  return stringValue.length > length
    ? `${stringValue.slice(0, length)}...`
    : stringValue;
};

const getStatusConfig = (status) => {
  return (
    STATUS_CONFIG?.[status] || {
      label: status || "Unknown",
      className: "text-slate-700 bg-slate-50 border-slate-200",
      icon: Activity,
    }
  );
};

/* =========================================================
   Reusable UI
========================================================= */

function StatusBadge({ status }) {
  const config = getStatusConfig(status);
  const Icon = config.icon;

  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] font-semibold ${config.className}`}
    >
      <Icon size={12} />
      {config.label}
    </span>
  );
}

function InstallmentStatusBadge({ status }) {
  return (
    <span
      className={`inline-flex rounded-full px-2.5 py-1 text-[10px] font-bold uppercase tracking-wide ${
        INSTALLMENT_STATUS_CONFIG?.[status] || "bg-slate-50 text-slate-700"
      }`}
    >
      {status || "UNKNOWN"}
    </span>
  );
}

function InfoItem({ label, value, icon: Icon, mono = false }) {
  return (
    <div className="min-w-0">
      <div className="mb-1 flex items-center gap-1.5 text-[11px] font-medium text-slate-500">
        {Icon && <Icon size={13} />}
        {label}
      </div>

      <div
        className={`truncate text-sm font-semibold text-slate-800 ${
          mono ? "font-mono text-[12px]" : ""
        }`}
        title={value || ""}
      >
        {value || "—"}
      </div>
    </div>
  );
}

function StatCard({ label, value, icon: Icon, subText }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-xs font-medium text-slate-500">{label}</p>
          <p className="mt-1 text-xl font-bold tracking-tight text-slate-900">
            {value}
          </p>

          {subText && (
            <p className="mt-1 text-[11px] text-slate-400">{subText}</p>
          )}
        </div>

        <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-blue-50 text-blue-600">
          <Icon size={18} />
        </div>
      </div>
    </div>
  );
}

function CopyButton({ value }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    if (!value) return;

    try {
      await navigator.clipboard.writeText(String(value));
      setCopied(true);

      setTimeout(() => {
        setCopied(false);
      }, 1500);
    } catch (error) {
      console.error("Copy failed:", error);
    }
  };

  return (
    <button
      type="button"
      onClick={handleCopy}
      className="ml-1 inline-flex items-center justify-center rounded-md p-1 text-slate-400 transition hover:bg-slate-100 hover:text-blue-600"
      title="Copy"
    >
      {copied ? <Check size={13} /> : <Copy size={13} />}
    </button>
  );
}

/* =========================================================
   Main Component
========================================================= */

function DetailedMandateReport() {
  const [reportData, setReportData] = useState([]);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [filters, setFilters] = useState({
    mandate_id: "",
    user_id: "",
    search: "",
    from_date: "",
    to_date: "",
    status: "",
  });

  const [appliedFilters, setAppliedFilters] = useState({});

  const [pagination, setPagination] = useState({
    total: 0,
    per_page: 10,
    current_page: 1,
    last_page: 1,
  });

  const [selectedMandate, setSelectedMandate] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);

  const [activeTab, setActiveTab] = useState("overview");

  const [expandedHistory, setExpandedHistory] = useState(null);
  const [expandedInstallment, setExpandedInstallment] = useState(null);

  const testNotification = async () => {
    console.log("Notification supported:", "Notification" in window);
    console.log("Current permission:", Notification.permission);

    if (!("Notification" in window)) {
      console.error("Notifications are NOT supported");
      return;
    }

    let permission = Notification.permission;

    if (permission === "default") {
      permission = await Notification.requestPermission();
      console.log("Permission after request:", permission);
    }

    if (permission !== "granted") {
      console.error("Notification permission is:", permission);
      return;
    }

    console.log("Creating notification...");

    try {
      const notification = new Notification("IDSPay Test", {
        body: "🎉 Mandate Report Fetched Successfully!",
      });

      notification.onshow = () => {
        console.log("✅ NOTIFICATION SHOW EVENT FIRED");
      };

      notification.onerror = (error) => {
        console.error("❌ NOTIFICATION ERROR:", error);
      };

      notification.onclose = () => {
        console.log("⚠️ NOTIFICATION CLOSED");
      };

      console.log("Notification object:", notification);
    } catch (error) {
      console.error("Notification creation failed:", error);
    }
  };
  /* =========================================================
     Fetch report
  ========================================================= */

  const fetchMandateReport = useCallback(
    async (page = 1, customFilters = appliedFilters) => {
      setLoading(true);
      setError("");

      try {
        const payload = {
          ...customFilters,
          page,
        };

        // Remove empty values
        Object.keys(payload).forEach((key) => {
          if (
            payload[key] === "" ||
            payload[key] === null ||
            payload[key] === undefined
          ) {
            delete payload[key];
          }
        });

        const res = await getDetailedMandateReport(payload);

        if (res?.status?.code === 200) {
          setReportData(res?.data || []);
          testNotification();

          setPagination({
            total: res?.pagination?.total || 0,
            per_page: res?.pagination?.per_page || 10,
            current_page: res?.pagination?.current_page || page,
            last_page: res?.pagination?.last_page || 1,
          });
        } else {
          setReportData([]);
          setError(
            res?.status?.message ||
              res?.message ||
              "Unable to fetch mandate report.",
          );
        }
      } catch (err) {
        console.error("Mandate report error:", err);
        setReportData([]);
        setError(
          err?.response?.data?.message ||
            err?.message ||
            "Something went wrong while fetching mandate report.",
        );
      } finally {
        setLoading(false);
      }
    },
    [appliedFilters],
  );

  useEffect(() => {
    fetchMandateReport(1, {});
  }, []);

  /* =========================================================
     Filters
  ========================================================= */

  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSearch = () => {
    setAppliedFilters(filters);
    fetchMandateReport(1, filters);
  };

  const handleReset = () => {
    const emptyFilters = {
      mandate_id: "",
      user_id: "",
      search: "",
      from_date: "",
      to_date: "",
      status: "",
    };

    setFilters(emptyFilters);
    setAppliedFilters({});
    fetchMandateReport(1, {});
  };

  const handlePageChange = (page) => {
    if (page < 1 || page > pagination.last_page || loading) return;

    fetchMandateReport(page, appliedFilters);
  };

  /* =========================================================
     Modal
  ========================================================= */

  const openMandate = (mandate) => {
    setSelectedMandate(mandate);
    setActiveTab("overview");
    setExpandedHistory(null);
    setExpandedInstallment(null);
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);

    setTimeout(() => {
      setSelectedMandate(null);
    }, 200);
  };

  /* =========================================================
     Summary
  ========================================================= */

  const reportSummary = useMemo(() => {
    const total = pagination.total || 0;

    const activated = reportData.filter(
      (item) => item?.status === "ACTIVATED",
    ).length;

    const created = reportData.filter(
      (item) => item?.status === "CREATED",
    ).length;

    const cancelled = reportData.filter(
      (item) => item?.status === "CANCELLED",
    ).length;

    const pageAmount = reportData.reduce(
      (sum, item) => sum + Number(item?.amount || 0),
      0,
    );

    return {
      total,
      activated,
      created,
      cancelled,
      pageAmount,
    };
  }, [reportData, pagination.total]);

  /* =========================================================
     Render
  ========================================================= */

  return (
    <div className="min-h-screen bg-slate-50 px-4 py-5 text-slate-900 sm:px-6 lg:px-8">
      <div className="mx-auto max-w-[1600px]">
        {/* =====================================================
            PAGE HEADER
        ===================================================== */}

        <div className="mb-6 flex flex-col justify-between gap-4 md:flex-row md:items-center">
          <div>
            <div className="mb-1 flex items-center gap-2">
              <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-blue-600 text-white shadow-sm">
                <CreditCard size={19} />
              </div>

              <h1 className="text-xl font-bold tracking-tight text-slate-900">
                Mandate Report
              </h1>
            </div>

            <p className="text-sm text-slate-500">
              Monitor mandates, customers, installments and payment activity.
            </p>
          </div>

          <button
            type="button"
            onClick={() => fetchMandateReport(pagination.current_page)}
            disabled={loading}
            className="inline-flex h-10 items-center justify-center gap-2 rounded-lg border border-slate-200 bg-white px-4 text-sm font-semibold text-slate-700 shadow-sm transition hover:border-blue-200 hover:bg-blue-50 hover:text-blue-700 disabled:cursor-not-allowed disabled:opacity-60"
          >
            <RotateCcw size={15} className={loading ? "animate-spin" : ""} />
            Refresh
          </button>
        </div>

        {/* =====================================================
            SUMMARY CARDS
        ===================================================== */}

        <div className="mb-6 grid grid-cols-2 gap-3 lg:grid-cols-4">
          <StatCard
            label="Total Mandates"
            value={reportSummary.total.toLocaleString("en-IN")}
            icon={CreditCard}
            subText="All matching records"
          />

          <StatCard
            label="Activated"
            value={reportSummary.activated}
            icon={CheckCircle2}
            subText="On current page"
          />

          <StatCard
            label="Created"
            value={reportSummary.created}
            icon={Clock}
            subText="On current page"
          />

          <StatCard
            label="Page Amount"
            value={formatCurrency(reportSummary.pageAmount)}
            icon={CircleDollarSign}
            subText="Current page total"
          />
        </div>

        {/* =====================================================
            FILTER CARD
        ===================================================== */}

        <div className="mb-6 rounded-xl border border-slate-200 bg-white shadow-sm">
          <div className="border-b border-slate-100 px-5 py-4">
            <div className="flex items-center gap-2">
              <Filter size={17} className="text-blue-600" />

              <div>
                <h2 className="text-sm font-bold text-slate-900">
                  Search & Filters
                </h2>

                <p className="text-[11px] text-slate-500">
                  Filter mandates using customer, date, status or mandate
                  details.
                </p>
              </div>
            </div>
          </div>

          <div className="p-5">
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-6">
              {/* Mandate ID */}
              <div>
                <label className="mb-1.5 block text-xs font-semibold text-slate-600">
                  Mandate ID
                </label>

                <input
                  type="text"
                  value={filters.mandate_id}
                  onChange={(e) =>
                    handleFilterChange("mandate_id", e.target.value)
                  }
                  placeholder="Enter mandate ID"
                  className="h-10 w-full rounded-lg border border-slate-200 bg-white px-3 text-sm text-slate-800 outline-none transition placeholder:text-slate-400 focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                />
              </div>

              {/* User ID */}
              <div>
                <label className="mb-1.5 block text-xs font-semibold text-slate-600">
                  User ID
                </label>

                <input
                  type="text"
                  value={filters.user_id}
                  onChange={(e) =>
                    handleFilterChange("user_id", e.target.value)
                  }
                  placeholder="e.g. APID0742"
                  className="h-10 w-full rounded-lg border border-slate-200 bg-white px-3 text-sm text-slate-800 outline-none transition placeholder:text-slate-400 focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                />
              </div>

              {/* Search */}
              <div>
                <label className="mb-1.5 block text-xs font-semibold text-slate-600">
                  Search
                </label>

                <div className="relative">
                  <Search
                    size={15}
                    className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
                  />

                  <input
                    type="text"
                    value={filters.search}
                    onChange={(e) =>
                      handleFilterChange("search", e.target.value)
                    }
                    placeholder="Mobile / name / reference"
                    className="h-10 w-full rounded-lg border border-slate-200 bg-white pl-9 pr-3 text-sm text-slate-800 outline-none transition placeholder:text-slate-400 focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                  />
                </div>
              </div>

              {/* From date */}
              <div>
                <label className="mb-1.5 block text-xs font-semibold text-slate-600">
                  From Date
                </label>

                <input
                  type="date"
                  value={filters.from_date}
                  onChange={(e) =>
                    handleFilterChange("from_date", e.target.value)
                  }
                  className="h-10 w-full rounded-lg border border-slate-200 bg-white px-3 text-sm text-slate-800 outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                />
              </div>

              {/* To date */}
              <div>
                <label className="mb-1.5 block text-xs font-semibold text-slate-600">
                  To Date
                </label>

                <input
                  type="date"
                  value={filters.to_date}
                  onChange={(e) =>
                    handleFilterChange("to_date", e.target.value)
                  }
                  className="h-10 w-full rounded-lg border border-slate-200 bg-white px-3 text-sm text-slate-800 outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                />
              </div>

              {/* Status */}
              <div>
                <label className="mb-1.5 block text-xs font-semibold text-slate-600">
                  Status
                </label>

                <select
                  value={filters.status}
                  onChange={(e) => handleFilterChange("status", e.target.value)}
                  className="h-10 w-full rounded-lg border border-slate-200 bg-white px-3 text-sm text-slate-800 outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                >
                  <option value="">All Statuses</option>
                  <option value="CREATED">Created</option>
                  <option value="ACTIVATED">Activated</option>
                  <option value="CANCELLED">Cancelled</option>
                  <option value="FAILED">Failed</option>
                  <option value="IN_PROGRESS">In Progress</option>
                </select>
              </div>
            </div>

            <div className="mt-4 flex flex-col justify-end gap-2 sm:flex-row">
              <button
                type="button"
                onClick={handleReset}
                className="inline-flex h-10 items-center justify-center gap-2 rounded-lg border border-slate-200 bg-white px-4 text-sm font-semibold text-slate-600 transition hover:bg-slate-50"
              >
                <RotateCcw size={15} />
                Reset
              </button>

              <button
                type="button"
                onClick={handleSearch}
                disabled={loading}
                className="inline-flex h-10 items-center justify-center gap-2 rounded-lg bg-blue-600 px-5 text-sm font-semibold text-white shadow-sm transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {loading ? (
                  <Loader2 size={15} className="animate-spin" />
                ) : (
                  <Search size={15} />
                )}
                Search Report
              </button>
            </div>
          </div>
        </div>

        {/* =====================================================
            REPORT TABLE
        ===================================================== */}

        <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
          {/* Table header */}
          <div className="flex flex-col justify-between gap-2 border-b border-slate-100 px-5 py-4 sm:flex-row sm:items-center">
            <div>
              <h2 className="text-sm font-bold text-slate-900">
                Mandate Records
              </h2>

              <p className="mt-0.5 text-[11px] text-slate-500">
                Showing{" "}
                {reportData.length > 0
                  ? `${
                      (pagination.current_page - 1) * pagination.per_page + 1
                    }–${Math.min(
                      pagination.current_page * pagination.per_page,
                      pagination.total,
                    )}`
                  : 0}{" "}
                of {pagination.total} records
              </p>
            </div>
          </div>

          {error && (
            <div className="m-4 flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
              <AlertCircle size={16} />
              {error}
            </div>
          )}

          <div className="overflow-x-auto">
            <table className="w-full min-w-[1200px] text-left">
              <thead>
                <tr className="border-b border-slate-200 bg-slate-50">
                  <th className="px-5 py-3 text-[11px] font-bold uppercase tracking-wide text-slate-500">
                    Mandate
                  </th>

                  <th className="px-5 py-3 text-[11px] font-bold uppercase tracking-wide text-slate-500">
                    Customer
                  </th>

                  <th className="px-5 py-3 text-[11px] font-bold uppercase tracking-wide text-slate-500">
                    Amount
                  </th>

                  <th className="px-5 py-3 text-[11px] font-bold uppercase tracking-wide text-slate-500">
                    Frequency
                  </th>

                  <th className="px-5 py-3 text-[11px] font-bold uppercase tracking-wide text-slate-500">
                    Status
                  </th>

                  <th className="px-5 py-3 text-[11px] font-bold uppercase tracking-wide text-slate-500">
                    Schedule
                  </th>

                  <th className="px-5 py-3 text-[11px] font-bold uppercase tracking-wide text-slate-500">
                    Installments
                  </th>

                  <th className="px-5 py-3 text-right text-[11px] font-bold uppercase tracking-wide text-slate-500">
                    Action
                  </th>
                </tr>
              </thead>

              <tbody className="divide-y divide-slate-100">
                {loading ? (
                  Array.from({ length: 6 }).map((_, index) => (
                    <tr key={index}>
                      {Array.from({ length: 8 }).map((__, cellIndex) => (
                        <td key={cellIndex} className="px-5 py-4">
                          <div className="h-4 animate-pulse rounded bg-slate-100" />
                        </td>
                      ))}
                    </tr>
                  ))
                ) : reportData.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="px-5 py-16 text-center">
                      <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-slate-100 text-slate-400">
                        <FileText size={21} />
                      </div>

                      <h3 className="mt-3 text-sm font-bold text-slate-800">
                        No mandates found
                      </h3>

                      <p className="mt-1 text-xs text-slate-500">
                        Try changing your filters and search again.
                      </p>
                    </td>
                  </tr>
                ) : (
                  reportData.map((mandate) => (
                    <tr
                      key={mandate.id || mandate.mandate_id}
                      className="group transition hover:bg-slate-50/80"
                    >
                      {/* Mandate */}
                      <td className="px-5 py-4">
                        <div className="flex items-center gap-3">
                          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-blue-50 text-blue-600">
                            <CreditCard size={16} />
                          </div>

                          <div className="min-w-0">
                            <div className="flex items-center">
                              <span
                                className="font-mono text-xs font-bold text-slate-800"
                                title={mandate.mandate_id}
                              >
                                {truncate(mandate.mandate_id, 18)}
                              </span>

                              <CopyButton value={mandate.mandate_id} />
                            </div>

                            <p className="mt-0.5 text-[10px] text-slate-400">
                              #{mandate.id} · {mandate.type || "REGULAR"}
                            </p>
                          </div>
                        </div>
                      </td>

                      {/* Customer */}
                      <td className="px-5 py-4">
                        <div>
                          <p className="text-sm font-semibold text-slate-800">
                            {mandate.customer?.name || "—"}
                          </p>

                          <div className="mt-1 flex items-center gap-1.5 text-[11px] text-slate-500">
                            <User size={12} />
                            {mandate.customer?.user_id || "—"}
                          </div>

                          <div className="mt-0.5 flex items-center gap-1.5 text-[11px] text-slate-400">
                            <Smartphone size={11} />
                            {maskMobile(mandate.customer?.mobile_number)}
                          </div>
                        </div>
                      </td>

                      {/* Amount */}
                      <td className="px-5 py-4">
                        <p className="text-sm font-bold text-slate-900">
                          {formatCurrency(mandate.amount)}
                        </p>

                        <p className="mt-1 text-[10px] text-slate-400">
                          Approval: {formatCurrency(mandate.approval_amount)}
                        </p>
                      </td>

                      {/* Frequency */}
                      <td className="px-5 py-4">
                        <span className="text-xs font-semibold text-slate-700">
                          {mandate.frequency || "—"}
                        </span>

                        <p className="mt-1 text-[10px] text-slate-400">
                          {mandate.mode || "—"}
                        </p>
                      </td>

                      {/* Status */}
                      <td className="px-5 py-4">
                        <StatusBadge status={mandate.status} />
                      </td>

                      {/* Dates */}
                      <td className="px-5 py-4">
                        <div className="text-xs font-semibold text-slate-700">
                          {formatDate(mandate.start_date)}
                        </div>

                        <div className="my-1 h-px w-5 bg-slate-200" />

                        <div className="text-[11px] text-slate-500">
                          {formatDate(mandate.end_date)}
                        </div>
                      </td>

                      {/* Installments */}
                      <td className="px-5 py-4">
                        <div className="flex items-center gap-2">
                          <div>
                            <p className="text-sm font-bold text-slate-800">
                              {mandate.installment_summary?.total ??
                                mandate.installment_count ??
                                0}
                            </p>

                            <p className="text-[10px] text-slate-400">
                              installments
                            </p>
                          </div>

                          <div className="h-8 w-px bg-slate-200" />

                          <div>
                            <p className="text-xs font-semibold text-emerald-600">
                              {mandate.installment_summary?.success || 0}
                            </p>

                            <p className="text-[10px] text-slate-400">
                              success
                            </p>
                          </div>
                        </div>
                      </td>

                      {/* Action */}
                      <td className="px-5 py-4 text-right">
                        <button
                          type="button"
                          onClick={() => openMandate(mandate)}
                          className="inline-flex h-9 items-center gap-1.5 rounded-lg border border-blue-200 bg-blue-50 px-3 text-xs font-bold text-blue-700 transition hover:bg-blue-600 hover:text-white"
                        >
                          <Eye size={14} />
                          View
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* =====================================================
              PAGINATION
          ===================================================== */}

          <div className="flex flex-col justify-between gap-3 border-t border-slate-100 px-5 py-4 sm:flex-row sm:items-center">
            <p className="text-xs text-slate-500">
              Page{" "}
              <span className="font-semibold text-slate-700">
                {pagination.current_page}
              </span>{" "}
              of{" "}
              <span className="font-semibold text-slate-700">
                {pagination.last_page}
              </span>
            </p>

            <div className="flex items-center gap-1.5">
              <button
                type="button"
                disabled={pagination.current_page <= 1 || loading}
                onClick={() => handlePageChange(pagination.current_page - 1)}
                className="flex h-8 w-8 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-600 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40"
              >
                <ChevronLeft size={15} />
              </button>

              {Array.from(
                {
                  length: Math.min(5, pagination.last_page),
                },
                (_, index) => {
                  let pageNumber;

                  if (pagination.last_page <= 5) {
                    pageNumber = index + 1;
                  } else if (pagination.current_page <= 3) {
                    pageNumber = index + 1;
                  } else if (
                    pagination.current_page >=
                    pagination.last_page - 2
                  ) {
                    pageNumber = pagination.last_page - 4 + index;
                  } else {
                    pageNumber = pagination.current_page - 2 + index;
                  }

                  return (
                    <button
                      key={pageNumber}
                      type="button"
                      onClick={() => handlePageChange(pageNumber)}
                      disabled={loading}
                      className={`h-8 min-w-8 rounded-lg px-2 text-xs font-semibold transition ${
                        pagination.current_page === pageNumber
                          ? "bg-blue-600 text-white shadow-sm"
                          : "border border-slate-200 bg-white text-slate-600 hover:bg-slate-50"
                      }`}
                    >
                      {pageNumber}
                    </button>
                  );
                },
              )}

              <button
                type="button"
                disabled={
                  pagination.current_page >= pagination.last_page || loading
                }
                onClick={() => handlePageChange(pagination.current_page + 1)}
                className="flex h-8 w-8 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-600 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40"
              >
                <ChevronRight size={15} />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* =========================================================
          DETAIL MODAL
      ========================================================= */}

      {modalOpen && selectedMandate && (
        <MandateDetailModal
          mandate={selectedMandate}
          activeTab={activeTab}
          setActiveTab={setActiveTab}
          expandedHistory={expandedHistory}
          setExpandedHistory={setExpandedHistory}
          expandedInstallment={expandedInstallment}
          setExpandedInstallment={setExpandedInstallment}
          onClose={closeModal}
        />
      )}
    </div>
  );
}

/* =========================================================
   DETAIL MODAL
========================================================= */

function MandateDetailModal({
  mandate,
  activeTab,
  setActiveTab,
  expandedHistory,
  setExpandedHistory,
  expandedInstallment,
  setExpandedInstallment,
  onClose,
}) {
  const tabs = [
    {
      id: "overview",
      label: "Overview",
      icon: FileText,
    },
    {
      id: "installments",
      label: "Installments",
      icon: Calendar,
      count: mandate.installments?.length || 0,
    },
    {
      id: "payees",
      label: "Payees",
      icon: Users,
      count: mandate.response?.payees?.length || 0,
    },
    {
      id: "history",
      label: "History",
      icon: History,
      count: mandate.histories?.length || 0,
    },
    {
      id: "raw",
      label: "Raw Data",
      icon: Activity,
    },
  ];

  return (
    <div className="fixed inset-0 z-[999] flex items-center justify-center bg-slate-900/50 p-3 backdrop-blur-sm sm:p-5">
      <div className="flex max-h-[94vh] w-full max-w-7xl flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-2xl">
        {/* =====================================================
            MODAL HEADER
        ===================================================== */}

        <div className="shrink-0 border-b border-slate-200 bg-white px-5 py-4 sm:px-6">
          <div className="flex items-start justify-between gap-4">
            <div className="flex min-w-0 items-center gap-3">
              <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-blue-50 text-blue-600">
                <CreditCard size={20} />
              </div>

              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <h2 className="text-base font-bold text-slate-900">
                    Mandate Details
                  </h2>

                  <StatusBadge status={mandate.status} />
                </div>

                <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-[11px] text-slate-500">
                  <span className="font-mono">{mandate.mandate_id}</span>

                  <span>•</span>

                  <span>{mandate.customer?.name || "Unknown Customer"}</span>

                  <span>•</span>

                  <span>{mandate.customer?.user_id || "—"}</span>
                </div>
              </div>
            </div>

            <button
              type="button"
              onClick={onClose}
              className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-slate-400 transition hover:bg-slate-100 hover:text-slate-700"
            >
              <X size={19} />
            </button>
          </div>

          {/* ===================================================
              TABS
          =================================================== */}

          <div className="mt-5 -mb-4 flex overflow-x-auto">
            {tabs.map((tab) => {
              const Icon = tab.icon;

              const active = activeTab === tab.id;

              return (
                <button
                  key={tab.id}
                  type="button"
                  onClick={() => setActiveTab(tab.id)}
                  className={`relative flex shrink-0 items-center gap-1.5 px-4 py-3 text-xs font-semibold transition ${
                    active
                      ? "text-blue-600"
                      : "text-slate-500 hover:text-slate-800"
                  }`}
                >
                  <Icon size={14} />

                  {tab.label}

                  {tab.count !== undefined && (
                    <span
                      className={`rounded-full px-1.5 py-0.5 text-[9px] ${
                        active
                          ? "bg-blue-100 text-blue-700"
                          : "bg-slate-100 text-slate-500"
                      }`}
                    >
                      {tab.count}
                    </span>
                  )}

                  {active && (
                    <span className="absolute bottom-0 left-2 right-2 h-0.5 rounded-full bg-blue-600" />
                  )}
                </button>
              );
            })}
          </div>
        </div>

        {/* =====================================================
            MODAL CONTENT
        ===================================================== */}

        <div className="min-h-0 flex-1 overflow-y-auto bg-slate-50 p-4 sm:p-6">
          {activeTab === "overview" && <OverviewTab mandate={mandate} />}

          {activeTab === "installments" && (
            <InstallmentsTab
              mandate={mandate}
              expandedInstallment={expandedInstallment}
              setExpandedInstallment={setExpandedInstallment}
            />
          )}

          {activeTab === "payees" && <PayeesTab mandate={mandate} />}

          {activeTab === "history" && (
            <HistoryTab
              mandate={mandate}
              expandedHistory={expandedHistory}
              setExpandedHistory={setExpandedHistory}
            />
          )}

          {activeTab === "raw" && <RawDataTab mandate={mandate} />}
        </div>

        {/* =====================================================
            MODAL FOOTER
        ===================================================== */}

        <div className="flex shrink-0 items-center justify-between border-t border-slate-200 bg-white px-5 py-3">
          <div className="text-[11px] text-slate-400">
            Created {formatDateTime(mandate.created_at)}
          </div>

          <button
            type="button"
            onClick={onClose}
            className="rounded-lg bg-slate-900 px-4 py-2 text-xs font-semibold text-white transition hover:bg-slate-800"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

/* =========================================================
   OVERVIEW TAB
========================================================= */

function OverviewTab({ mandate }) {
  const payer =
    mandate?.response?.payer || mandate?.installments?.[0]?.response?.payer;

  const payees = mandate?.response?.payees || [];

  return (
    <div className="space-y-5">
      {/* Summary */}
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <StatCard
          label="Mandate Amount"
          value={formatCurrency(mandate.amount)}
          icon={CircleDollarSign}
        />

        <StatCard
          label="Approval Amount"
          value={formatCurrency(mandate.approval_amount)}
          icon={CreditCard}
        />

        <StatCard
          label="Installments"
          value={
            mandate.installment_summary?.total ?? mandate.installment_count ?? 0
          }
          icon={Calendar}
        />

        <StatCard
          label="Frequency"
          value={mandate.frequency || "—"}
          icon={Clock}
        />
      </div>

      {/* Customer + mandate */}
      <div className="grid grid-cols-1 gap-5 xl:grid-cols-2">
        {/* Customer */}
        <section className="rounded-xl border border-slate-200 bg-white shadow-sm">
          <div className="border-b border-slate-100 px-5 py-4">
            <h3 className="flex items-center gap-2 text-sm font-bold text-slate-900">
              <User size={16} className="text-blue-600" />
              Customer Information
            </h3>
          </div>

          <div className="grid grid-cols-1 gap-5 p-5 sm:grid-cols-2">
            <InfoItem
              label="Customer Name"
              value={mandate.customer?.name}
              icon={User}
            />

            <InfoItem
              label="User ID"
              value={mandate.customer?.user_id}
              icon={Hash}
            />

            <InfoItem
              label="Mobile Number"
              value={mandate.customer?.mobile_number}
              icon={Smartphone}
            />

            <InfoItem
              label="Email Address"
              value={mandate.customer?.email_address}
              icon={Mail}
            />
          </div>
        </section>

        {/* Mandate */}
        <section className="rounded-xl border border-slate-200 bg-white shadow-sm">
          <div className="border-b border-slate-100 px-5 py-4">
            <h3 className="flex items-center gap-2 text-sm font-bold text-slate-900">
              <CreditCard size={16} className="text-blue-600" />
              Mandate Information
            </h3>
          </div>

          <div className="grid grid-cols-1 gap-5 p-5 sm:grid-cols-2">
            <InfoItem label="Mandate ID" value={mandate.mandate_id} mono />

            <InfoItem
              label="Mandate Account"
              value={mandate.mandate_account_number}
              mono
            />

            <InfoItem label="Reference ID" value={mandate.reference_id} mono />

            <InfoItem label="Reference Type" value={mandate.reference_type} />

            <InfoItem label="Mode" value={mandate.mode} />

            <InfoItem label="Time Zone" value={mandate.time_zone} />
          </div>
        </section>
      </div>

      {/* Schedule */}
      <section className="rounded-xl border border-slate-200 bg-white shadow-sm">
        <div className="border-b border-slate-100 px-5 py-4">
          <h3 className="flex items-center gap-2 text-sm font-bold text-slate-900">
            <Calendar size={16} className="text-blue-600" />
            Schedule & Payment Configuration
          </h3>
        </div>

        <div className="grid grid-cols-2 gap-5 p-5 sm:grid-cols-3 lg:grid-cols-6">
          <InfoItem label="Start Date" value={formatDate(mandate.start_date)} />

          <InfoItem label="End Date" value={formatDate(mandate.end_date)} />

          <InfoItem label="Frequency" value={mandate.frequency} />

          <InfoItem
            label="Installment Count"
            value={mandate.installment_count}
          />

          <InfoItem
            label="Advance Amount"
            value={formatCurrency(mandate.advance_amount)}
          />

          <InfoItem
            label="Total Amount"
            value={formatCurrency(mandate.amount)}
          />
        </div>

        {mandate.description && (
          <div className="border-t border-slate-100 px-5 py-4">
            <p className="mb-1 text-[11px] font-semibold text-slate-500">
              Description
            </p>

            <p className="text-sm text-slate-700">{mandate.description}</p>
          </div>
        )}
      </section>

      {/* Payer */}
      {payer && (
        <section className="rounded-xl border border-slate-200 bg-white shadow-sm">
          <div className="border-b border-slate-100 px-5 py-4">
            <h3 className="flex items-center gap-2 text-sm font-bold text-slate-900">
              <WalletCards size={16} className="text-blue-600" />
              Payer
            </h3>
          </div>

          <div className="grid grid-cols-1 gap-5 p-5 sm:grid-cols-2 lg:grid-cols-4">
            <InfoItem label="Account ID" value={payer.account_id} mono />

            <InfoItem label="Account Name" value={payer.account?.name} />

            <InfoItem label="Mobile" value={payer.account?.mobile_number} />

            <InfoItem
              label="Amount"
              value={formatCurrency(payer.amount?.value)}
            />
          </div>
        </section>
      )}

      {/* Created / updated */}
      <section className="rounded-xl border border-slate-200 bg-white shadow-sm">
        <div className="border-b border-slate-100 px-5 py-4">
          <h3 className="text-sm font-bold text-slate-900">
            Audit Information
          </h3>
        </div>

        <div className="grid grid-cols-1 gap-5 p-5 sm:grid-cols-2 lg:grid-cols-4">
          <InfoItem label="Created By" value={mandate.created_by?.name} />

          <InfoItem
            label="Created User ID"
            value={mandate.created_by?.user_id}
          />

          <InfoItem
            label="Created At"
            value={formatDateTime(mandate.created_at)}
          />

          <InfoItem
            label="Updated At"
            value={formatDateTime(mandate.updated_at)}
          />
        </div>
      </section>
    </div>
  );
}

/* =========================================================
   INSTALLMENTS TAB
========================================================= */

function InstallmentsTab({
  mandate,
  expandedInstallment,
  setExpandedInstallment,
}) {
  const installments = mandate?.installments || [];

  const summary = mandate?.installment_summary || {};

  return (
    <div className="space-y-5">
      {/* Summary */}
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <StatCard
          label="Total"
          value={summary.total ?? installments.length}
          icon={Calendar}
        />

        {/* <StatCard label="Pending" value={summary.pending ?? 0} icon={Clock} /> */}

        <StatCard
          label="Success"
          value={summary.success ?? 0}
          icon={CheckCircle2}
        />

        <StatCard label="Failed" value={summary.failed ?? 0} icon={XCircle} />

        <StatCard
          label="Total Amount"
          value={formatCurrency(summary.total_amount)}
          icon={CircleDollarSign}
        />
      </div>

      {/* Table */}
      <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
        <div className="border-b border-slate-100 px-5 py-4">
          <h3 className="text-sm font-bold text-slate-900">
            Installment Schedule
          </h3>
        </div>

        {installments.length === 0 ? (
          <div className="px-5 py-12 text-center text-sm text-slate-500">
            No installment records available.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[900px]">
              <thead>
                <tr className="border-b border-slate-200 bg-slate-50">
                  <th className="px-5 py-3 text-left text-[10px] font-bold uppercase tracking-wide text-slate-500">
                    #
                  </th>

                  <th className="px-5 py-3 text-left text-[10px] font-bold uppercase tracking-wide text-slate-500">
                    Installment ID
                  </th>

                  <th className="px-5 py-3 text-left text-[10px] font-bold uppercase tracking-wide text-slate-500">
                    Amount
                  </th>

                  <th className="px-5 py-3 text-left text-[10px] font-bold uppercase tracking-wide text-slate-500">
                    Due Date
                  </th>

                  <th className="px-5 py-3 text-left text-[10px] font-bold uppercase tracking-wide text-slate-500">
                    Scheduled
                  </th>

                  <th className="px-5 py-3 text-left text-[10px] font-bold uppercase tracking-wide text-slate-500">
                    Status
                  </th>

                  <th className="px-5 py-3 text-right text-[10px] font-bold uppercase tracking-wide text-slate-500">
                    Details
                  </th>
                </tr>
              </thead>

              <tbody className="divide-y divide-slate-100">
                {installments.map((installment, index) => {
                  const expanded = expandedInstallment === installment.id;

                  return (
                    <React.Fragment key={installment.id || index}>
                      <tr className="hover:bg-slate-50">
                        <td className="px-5 py-4 text-xs font-bold text-slate-700">
                          {installment.installment_number || index + 1}
                        </td>

                        <td className="px-5 py-4">
                          <span
                            className="font-mono text-[11px] text-slate-700"
                            title={installment.installment_id}
                          >
                            {truncate(installment.installment_id, 25)}
                          </span>
                        </td>

                        <td className="px-5 py-4 text-xs font-bold text-slate-800">
                          {formatCurrency(installment.amount)}
                        </td>

                        <td className="px-5 py-4 text-xs text-slate-600">
                          {formatDate(installment.due_date)}
                        </td>

                        <td className="px-5 py-4 text-xs text-slate-600">
                          {formatDate(installment.scheduled_date)}
                        </td>

                        <td className="px-5 py-4">
                          <InstallmentStatusBadge status={installment.status} />
                          <div className="text-[10px] ml-2 mt-1">{installment?.error_code}</div>
                        </td>

                        <td className="px-5 py-4 text-right">
                          <button
                            type="button"
                            onClick={() =>
                              setExpandedInstallment(
                                expanded ? null : installment.id,
                              )
                            }
                            className="inline-flex h-8 items-center gap-1 rounded-lg border border-slate-200 bg-white px-2.5 text-[11px] font-semibold text-slate-600 hover:bg-slate-50"
                          >
                            {expanded ? (
                              <>
                                <ChevronUp size={13} />
                                Hide
                              </>
                            ) : (
                              <>
                                <ChevronDown size={13} />
                                Details
                              </>
                            )}
                          </button>
                        </td>
                      </tr>

                      {expanded && (
                        <tr>
                          <td colSpan={7} className="bg-slate-50 px-5 py-4">
                            <InstallmentDetails installment={installment} />
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function InstallmentDetails({ installment }) {
  const payer = installment?.response?.payer;
  const payees = installment?.response?.payees || [];

  return (
    <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
      <div className="rounded-lg border border-slate-200 bg-white p-4">
        <p className="mb-3 text-xs font-bold text-slate-800">
          Installment Details
        </p>

        <div className="space-y-3">
          <InfoItem label="Description" value={installment.description} />

          <InfoItem
            label="Reference ID"
            value={installment.reference_id}
            mono
          />

          <InfoItem label="MMS ID" value={installment.mms_id} mono />

          <InfoItem
            label="Payment Order ID"
            value={installment.payment_order_id}
            mono
          />
        </div>
      </div>

      <div className="rounded-lg border border-slate-200 bg-white p-4">
        <p className="mb-3 text-xs font-bold text-slate-800">Payer</p>

        {payer ? (
          <div className="space-y-3">
            <InfoItem label="Name" value={payer.account?.name} />

            <InfoItem label="Mobile" value={payer.account?.mobile_number} />

            <InfoItem
              label="Amount"
              value={formatCurrency(payer.amount?.value)}
            />
          </div>
        ) : (
          <p className="text-xs text-slate-400">No payer information.</p>
        )}
      </div>

      <div className="rounded-lg border border-slate-200 bg-white p-4">
        <p className="mb-3 text-xs font-bold text-slate-800">Payees</p>

        <div className="space-y-3">
          {payees.length === 0 ? (
            <p className="text-xs text-slate-400">No payees.</p>
          ) : (
            payees.map((payee, index) => (
              <div
                key={payee.account_id || index}
                className="rounded-lg border border-slate-100 bg-slate-50 p-3"
              >
                <p className="text-xs font-semibold text-slate-800">
                  {payee.account?.name || "Unknown"}
                </p>

                <p className="mt-1 text-[10px] text-slate-500">
                  {payee.tag || "—"}
                </p>

                <p className="mt-1 text-xs font-bold text-blue-600">
                  {formatCurrency(payee.amount?.value)}
                </p>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}

/* =========================================================
   PAYEES TAB
========================================================= */

function PayeesTab({ mandate }) {
  const payees = mandate?.response?.payees || [];

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
        {payees.length === 0 ? (
          <div className="col-span-full rounded-xl border border-slate-200 bg-white px-5 py-12 text-center text-sm text-slate-500">
            No payees available.
          </div>
        ) : (
          payees.map((payee, index) => (
            <div
              key={payee.account_id || index}
              className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm"
            >
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-center gap-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-blue-50 text-blue-600">
                    <Users size={17} />
                  </div>

                  <div>
                    <p className="text-sm font-bold text-slate-800">
                      {payee.account?.name || "Unknown Payee"}
                    </p>

                    <p className="mt-0.5 text-[10px] text-slate-400">
                      {payee.tag || "—"}
                    </p>
                  </div>
                </div>

                <span className="text-sm font-bold text-blue-600">
                  {formatCurrency(payee.amount?.value)}
                </span>
              </div>

              <div className="mt-5 grid grid-cols-1 gap-4 border-t border-slate-100 pt-4">
                <InfoItem label="Account ID" value={payee.account_id} mono />

                <InfoItem
                  label="Mobile Number"
                  value={payee.account?.mobile_number}
                />

                <InfoItem
                  label="Instrument ID"
                  value={payee.instrument_id}
                  mono
                />
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

/* =========================================================
   HISTORY TAB
========================================================= */

function HistoryTab({ mandate, expandedHistory, setExpandedHistory }) {
  const histories = mandate?.histories || [];

  return (
    <div className="space-y-4">
      {histories.length === 0 ? (
        <div className="rounded-xl border border-slate-200 bg-white px-5 py-12 text-center text-sm text-slate-500">
          No history available.
        </div>
      ) : (
        histories.map((history, index) => {
          const expanded = expandedHistory === history.id;

          return (
            <div
              key={history.id || index}
              className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm"
            >
              <div className="flex items-start gap-4 p-5">
                <div className="relative flex flex-col items-center">
                  <div className="flex h-9 w-9 items-center justify-center rounded-full bg-blue-50 text-blue-600">
                    <History size={16} />
                  </div>

                  {index !== histories.length - 1 && (
                    <div className="absolute top-10 h-12 w-px bg-slate-200" />
                  )}
                </div>

                <div className="min-w-0 flex-1">
                  <div className="flex flex-col justify-between gap-2 sm:flex-row sm:items-start">
                    <div>
                      <div className="flex flex-wrap items-center gap-2">
                        <h3 className="text-sm font-bold text-slate-800">
                          {history.message || "History Event"}
                        </h3>

                        <StatusBadge status={history.status} />
                      </div>

                      <p className="mt-1 font-mono text-[10px] text-slate-400">
                        {history.type || "—"}
                      </p>
                    </div>

                    <div className="text-left sm:text-right">
                      <p className="text-[11px] font-semibold text-slate-600">
                        {formatDateTime(history.created_at)}
                      </p>

                      <p className="mt-1 text-[10px] text-slate-400">
                        By {history.created_by?.name || "System"}
                      </p>
                    </div>
                  </div>

                  <button
                    type="button"
                    onClick={() =>
                      setExpandedHistory(expanded ? null : history.id)
                    }
                    className="mt-4 inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-[11px] font-semibold text-slate-600 hover:bg-slate-50"
                  >
                    {expanded ? (
                      <>
                        <ChevronUp size={13} />
                        Hide Details
                      </>
                    ) : (
                      <>
                        <ChevronDown size={13} />
                        View Details
                      </>
                    )}
                  </button>
                </div>
              </div>

              {expanded && (
                <div className="border-t border-slate-100 bg-slate-50 p-5">
                  <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
                    <JsonBox title="Request" data={history.request} />

                    <JsonBox title="Response" data={history.response} />
                  </div>
                </div>
              )}
            </div>
          );
        })
      )}
    </div>
  );
}

/* =========================================================
   RAW DATA TAB
========================================================= */

function RawDataTab({ mandate }) {
  return (
    <div className="space-y-5">
      <JsonBox title="Request" data={mandate.request} />

      <JsonBox title="Gateway / Response" data={mandate.response} />
    </div>
  );
}

function JsonBox({ title, data }) {
  return (
    <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
      <div className="flex items-center justify-between border-b border-slate-100 px-5 py-3">
        <h3 className="text-xs font-bold text-slate-800">{title}</h3>

        <CopyButton value={data ? JSON.stringify(data, null, 2) : ""} />
      </div>

      <div className="max-h-[500px] overflow-auto bg-slate-950 p-4">
        <pre className="whitespace-pre-wrap break-words font-mono text-[11px] leading-5 text-slate-200">
          {data ? JSON.stringify(data, null, 2) : "No data available"}
        </pre>
      </div>
    </div>
  );
}

export default DetailedMandateReport;

